import { openDB } from "idb";
import { EVENTS_DB_NAME, REQUIRED_EVENTSAPP_STORES } from "../constants";

/**
 * Initialize IndexedDB instance for the events application.
 *
 * NOTE:
 * - Database version MUST be incremented for all schema changes aligned with {@link REQUIRED_EVENTSAPP_STORES}.
 * - Schema updates (store creation, index changes, removals) are handled exclusively in the `upgrade` block.
 * - `ensureDatabaseIntegrity` will throw if the runtime schema does not match the expected configuration.
 * - No automatic runtime schema mutation is performed.
 *
 * Migration annotations:
 * v5:
 *   - added expanded lookup stores and generic offline data store
 *
 * v6:
 *   - added faqs store
 *   - added eventslisting store
 *
 * v7 (pattern):
 *   - add new stores via REQUIRED_EVENTSAPP_STORES AND bump version
 *   - remove deprecated stores explicitly in upgrade()
 *   - apply index updates within upgrade()
 *   - ensure all schema changes are backward-compatible or migrated
 * v8 (pattern):
 *   - added event participant-type store
 *   - procinces and districts store
 */
export async function initDB(dbName = EVENTS_DB_NAME) {
  return openDB(dbName, 8, {
    upgrade(db, oldVersion, newVersion, transaction) {

      REQUIRED_EVENTSAPP_STORES.forEach(name => {
        createDbStore(db, name);
      });

      if (db.objectStoreNames.contains("offlineData")) {
        const store = transaction.objectStore("offlineData");
        if (!store.indexNames.contains("bucket")) {
          store.createIndex("bucket", "bucket", { unique: false });
        }
        if (!store.indexNames.contains("syncState")) {
          store.createIndex("syncState", "syncState", { unique: false });
        }
      }

      if (db.objectStoreNames.contains("statuses")) {
        db.deleteObjectStore("statuses");
      }
    },
  });
}

export function createDbStore(db, storeName) {
  if (db.objectStoreNames.contains(storeName)) {
    return; // early exit (clear intent)
  }

  if (storeName === "lookupMeta") {
    db.createObjectStore(storeName, { keyPath: "name" });
    return;
  }

  if (storeName === "offlineData") {
    const store = db.createObjectStore(storeName, { keyPath: "id" });
    store.createIndex("bucket", "bucket", { unique: false });
    store.createIndex("syncState", "syncState", { unique: false });
    return;
  }

  // default case
  db.createObjectStore(storeName, { keyPath: "id" });
}

/**
 * User Records Operations
 */
export async function createLocalRecord(record) {
  const db = await initDB();
  return await db.put("userRecords", record);
}

export async function deleteLocalRecord(id) {
  const db = await initDB();
  return await db.delete("userRecords", id);
}

export async function clearStore(storeName) {
  const db = await initDB();

  if (!db.objectStoreNames.contains(storeName)) {
    return; // silently ignore
  }

  const tx = db.transaction(storeName, "readwrite");
  await tx.store.clear();
  await tx.done;
}

/**
 * Maintenance: Full Wipe
 */
export async function wipeDatabase(dbName = EVENTS_DB_NAME) {
  const db = await initDB();
  db.close(); // Close connection before deletion
  return new Promise((resolve, reject) => {
    const req = indexedDB.deleteDatabase(dbName);
    req.onsuccess = () => resolve(true);
    req.onerror = () => reject(new Error("Wipe failed"));
    req.onblocked = () => reject(new Error("Wipe blocked - close other tabs"));
  });
}

/**
 * Lookups & Meta
 */
export async function saveLookupEntries(storeName, entries) {
  const db = await initDB();
  const tx = db.transaction(storeName, "readwrite");
  await tx.store.clear();

  const normalized = (entries || []).map((entry, index) => {
    if (entry && typeof entry === "object") {
      return {
        ...entry,
        id: entry.id ?? entry.key ?? entry.code ?? `${storeName}-${index}`,
      };
    }

    return {
      id: `${storeName}-${index}`,
      value: entry,
    };
  });

  for (const entry of normalized) {
    await tx.store.put(entry);
  }

  await tx.done;
}

export async function getLookupEntries(storeName) {
  const db = await initDB();
  return await db.getAll(storeName);
}

export async function saveLookupMeta(name, lastModified) {
  const db = await initDB();
  await db.put("lookupMeta", { name, lastModified });
}

export async function getLookupMeta(name) {
  const db = await initDB();
  return await db.get("lookupMeta", name);
}

export async function saveLookupEntriesWithMeta(storeName, entries, lastModified= Date.now().toString()) {
  // Concurrently save immediately
  Promise.all([
    saveLookupEntries(storeName, entries),
    saveLookupMeta(storeName, lastModified),
  ]).catch(err => console.warn("Failed to update lookup cache", err))
}

/**
 * Generic Offline Data Operations
 */
export async function saveOfflineData(bucket, payload, options = {}) {
  const db = await initDB();
  const id = options.id || payload?.id || `${bucket}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const record = {
    id,
    bucket,
    payload,
    syncState: options.syncState || "pending",
    createdAt: options.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    meta: options.meta || {},
  };

  await db.put("offlineData", record);
  return record;
}

export async function getOfflineData(bucket) {
  const db = await initDB();
  return await db.getAllFromIndex("offlineData", "bucket", bucket);
}

export async function getOfflineDataBySyncState(syncState) {
  const db = await initDB();
  return await db.getAllFromIndex("offlineData", "syncState", syncState);
}

export async function updateOfflineDataSyncState(id, syncState, meta = {}) {
  const db = await initDB();
  const existing = await db.get("offlineData", id);
  if (!existing) {
    return null;
  }

  const updated = {
    ...existing,
    syncState,
    updatedAt: new Date().toISOString(),
    meta: {
      ...(existing.meta || {}),
      ...meta,
    },
  };

  await db.put("offlineData", updated);
  return updated;
}

export async function deleteOfflineData(id) {
  const db = await initDB();
  await db.delete("offlineData", id);
}

export async function clearOfflineData(bucket) {
  const db = await initDB();
  const tx = db.transaction("offlineData", "readwrite");
  const index = tx.store.index("bucket");
  const keys = await index.getAllKeys(bucket);

  for (const key of keys) {
    await tx.store.delete(key);
  }

  await tx.done;
}

export async function checkDatabaseIntegrity() {
  try {
    const db = await initDB();

    const currentStores = Array.from(db.objectStoreNames);
    const storeSet = new Set(currentStores);

    const missing = REQUIRED_EVENTSAPP_STORES.filter(
      s => !storeSet.has(s)
    );

    return {
      healthy: missing.length === 0,
      missing,
      version: db.version,
    };
  } catch (error) {
    return {
      healthy: false,
      error: error.message,
    };
  }
}

export async function ensureDatabaseIntegrity() {
  const check = await checkDatabaseIntegrity();

  if (!check.healthy) {
    console.error("[DB] Schema mismatch detected:", check);

    throw new Error(
      `[DB] IndexedDB schema is out of sync. Missing stores: ${check.missing.join(", ")}. ` +
      `Please bump DB version and handle migration in initDB().`
    );
  }

  return check;
}

const estimateBytes = (value) => {
  try {
    return JSON.stringify(value || "").length * 2;
  } catch {
    return 0;
  }
};

export async function getOfflineDataSyncMetrics() {
  try {
    const db = await initDB();
    const records = await db.getAll("offlineData");
    const pendingRecords = (records || []).filter((record) => record?.syncState !== "synced");

    return {
      pendingCount: pendingRecords.length,
      estimatedSizeBytes: pendingRecords.reduce((acc, record) => acc + estimateBytes(record), 0),
      bySyncState: pendingRecords.reduce((acc, record) => {
        const state = record?.syncState || "unknown";
        acc[state] = Number(acc[state] || 0) + 1;
        return acc;
      }, {}),
      lastUpdated: new Date().toISOString(),
    };
  } catch {
    return {
      pendingCount: 0,
      estimatedSizeBytes: 0,
      bySyncState: {},
      lastUpdated: new Date().toISOString(),
    };
  }
}